import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "./Routes/ProtectedRoute";
import { getTenantSlug } from "./utils/tenant";

// Eager: tiny + always reachable. Lazy: everything bulky or role-gated.
import App from "./App";
import NotFound from "./Pages/NotFound/NotFound";

const Unauthorized      = lazy(() => import("./Pages/Unauthorized/unauthorized"));
const AuthPage          = lazy(() => import("./Pages/Auth/AuthPage"));
const Onboarding        = lazy(() => import("./Pages/Onboarding/Onboarding"));
const Maintenance       = lazy(() => import("./Pages/Maintenance/Maintenance"));
const LegalPage         = lazy(() => import("./Pages/Landing/LegalPage"));
const Company           = lazy(() => import("./Pages/Company/Company"));
const StorefrontLayout  = lazy(() => import("./Pages/Storefront/StorefrontLayout"));
const SiteHome          = lazy(() => import("./Pages/Storefront/pages/Home"));
const SiteAbout         = lazy(() => import("./Pages/Storefront/pages/About"));
const SiteProducts      = lazy(() => import("./Pages/Storefront/pages/Products"));
const SiteServices      = lazy(() => import("./Pages/Storefront/pages/Services"));
const SiteContact       = lazy(() => import("./Pages/Storefront/pages/Contact"));
const MerchantLogin     = lazy(() => import("./Pages/MerchantPortal/MerchantLogin"));
const StaffLogin = lazy(() => import("./Pages/Staff/StaffLogin"));
const MerchantPortal    = lazy(() => import("./Pages/MerchantPortal/MerchantPortal"));
const MerchantHistory   = lazy(() => import("./Pages/MerchantPortal/MerchantHistory"));
const RenewPage         = lazy(() => import("./Pages/Billing/BillingPages").then((m) => ({ default: m.RenewPage })));
const PayLinkPage       = lazy(() => import("./Pages/Billing/BillingPages").then((m) => ({ default: m.PayLinkPage })));
const BillingResultPage = lazy(() => import("./Pages/Billing/BillingPages").then((m) => ({ default: m.BillingResultPage })));
const AdminLayout       = lazy(() => import("./Pages/Super Admin/AdminLayout"));
const Companies         = lazy(() => import("./Pages/Super Admin/Companies"));
const CompanyDetails    = lazy(() => import("./Pages/Super Admin/CompanyDetails"));
const EmployeeProfile   = lazy(() => import("./Pages/Super Admin/EmployeeProfile"));
const AuditLogs         = lazy(() => import("./Pages/Super Admin/AuditLogs"));
const SystemHealth      = lazy(() => import("./Pages/Super Admin/SystemHealth"));
const Notifications     = lazy(() => import("./Pages/Super Admin/Notifications"));
const HomeContent       = lazy(() => import("./Pages/Super Admin/HomeContent"));
const Dashboard         = lazy(() => import("./Pages/Super Admin/Dashboard"));
const Billing           = lazy(() => import("./Pages/Super Admin/Billing"));
const Referrals         = lazy(() => import("./Pages/Super Admin/Referrals"));
const Plans             = lazy(() => import("./Pages/Super Admin/Plans"));
const Reports           = lazy(() => import("./Pages/Super Admin/Reports"));
const Settings          = lazy(() => import("./Pages/Super Admin/Settings"));
const Profile           = lazy(() => import("./Pages/Super Admin/Profile"));
const Tickets           = lazy(() => import("./Pages/Super Admin/Tickets"));
const TicketDetails     = lazy(() => import("./Pages/Super Admin/TicketDetails"));

// Lightweight fallback shown while a route chunk loads. Centered + RTL-safe.
const RouteFallback = () => (
    <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        minHeight: "60vh", color: "#64748b", gap: 10,
    }}>
        <Loader2 size={20} style={{ animation: "spin 0.9s linear infinite" }} />
        <span>جاري التحميل…</span>
    </div>
);

// Wrap any lazy element in a single Suspense boundary so the fallback
// renders during chunk fetch and code-split errors don't crash the tree.
const lazyRoute = (element) => (
    <Suspense fallback={<RouteFallback />}>{element}</Suspense>
);

// The invitation opens from one graphite screen onto another, so its chunk loads
// on the same ground. The light fallback above would flash white between them.
const InvitationFallback = () => (
    <div style={{ minHeight: "100svh", background: "var(--gr-950)" }} />
);

// Each storefront answers on its own subdomain (<slug>.example.com), so on a
// tenant host the site owns the root and the platform routes are not mounted
// at all. The /site/<slug> tree stays available for the workspace preview and
// for environments without wildcard DNS.
const siteChildren = [
    { index: true,          element: lazyRoute(<SiteHome />) },
    { path: "about",        element: lazyRoute(<SiteAbout />) },
    { path: "products",     element: lazyRoute(<SiteProducts />) },
    { path: "services",     element: lazyRoute(<SiteServices />) },
    { path: "contact",      element: lazyRoute(<SiteContact />) },
];

const tenantSlug = getTenantSlug();

const router = createBrowserRouter([
    ...(tenantSlug
        ? [{ path: "/", element: lazyRoute(<StorefrontLayout />), children: siteChildren }]
        : [{ path: "/", element: <App /> }]),
    { path: "/site/:slug", element: lazyRoute(<StorefrontLayout />), children: siteChildren },

    // The short form: elrayes-ac.com/<slug>.
    //
    // Subdomains need a wildcard DNS record, a wildcard vhost and a wildcard
    // certificate, none of which exist on this deployment — <slug>.<apex>
    // does not resolve at all. This gives every storefront a working public
    // address under the certificate the apex already has.
    //
    // React Router ranks a static segment above a dynamic one, so /auth and
    // /company keep winning over /:slug on their own. What it does NOT do is
    // rank /:slug below the "*" catch-all, so without RESERVED_PATHS a typo
    // like /copmany would go to the storefront lookup and answer "الموقع غير
    // موجود" instead of the application's own 404.
    {
        path: "/:slug",
        element: lazyRoute(<StorefrontLayout pathMode="root" />),
        children: siteChildren,
    },

    { path: "/unauthorized",  element: lazyRoute(<Unauthorized />) },
    { path: "*",              element: <NotFound /> },
    { path: "/auth",          element: lazyRoute(<AuthPage />) },
    // Sign-up with an invitation code. Public, but useless without the pass that
    // /auth issues for a good code; the page sends everyone else back there.
    {
        path: "/onboarding",
        element: <Suspense fallback={<InvitationFallback />}><Onboarding /></Suspense>,
    },
    { path: "/maintenance",   element: lazyRoute(<Maintenance />) },
    // The platform's legal pages, ahead of /:slug by the ranking above. A storefront cannot
    // claim either name: Back-End/Controller/websiteSlug.js and utils/seo.js reserve them.
    { path: "/privacy",       element: lazyRoute(<LegalPage kind="privacy" />) },
    { path: "/terms",         element: lazyRoute(<LegalPage kind="terms" />) },
    // Online subscription payment (Paymob) — public: a lapsed owner renewing
    // from the login screen, an admin's payment link, and Paymob's return.
    { path: "/renew",          element: lazyRoute(<RenewPage />) },
    { path: "/pay/:token",     element: lazyRoute(<PayLinkPage />) },
    { path: "/billing/result", element: lazyRoute(<BillingResultPage />) },

    {
        path: "/company",
        element: lazyRoute(
            <ProtectedRoute allowedRole="user">
                <Company />
            </ProtectedRoute>
        ),
    },

    // Merchant portal — separate auth (its own token in localStorage.merchant_token).
    // Each component manages its own redirect, no ProtectedRoute wrapper needed.
    // The field team's entrance. Same credentials and same session as
    // /auth — a separate door, not a separate lock.
    { path: "/staff",            element: lazyRoute(<StaffLogin />) },
    { path: "/merchant/login",   element: lazyRoute(<MerchantLogin />) },
    { path: "/merchant",         element: lazyRoute(<MerchantPortal />) },
    { path: "/merchant/history", element: lazyRoute(<MerchantHistory />) },

    // The super-admin module: ONE layout route. The sidebar and topbar stay
    // mounted across navigation, and a lazily-loaded page shows its loading
    // state inside the content area (AdminLayout's own Suspense) instead of
    // blanking the whole shell.
    {
        path: "/admin",
        element: lazyRoute(
            <ProtectedRoute allowedRole="admin">
                <AdminLayout />
            </ProtectedRoute>
        ),
        children: [
            { index: true,                                    element: <Navigate to="/admin/dashboard" replace /> },
            { path: "dashboard",                              element: <Dashboard /> },
            { path: "companies",                              element: <Companies /> },
            { path: "companies/:id",                          element: <CompanyDetails /> },
            { path: "companies/:id/employees/:employeeId",    element: <EmployeeProfile /> },
            { path: "billing",                                element: <Billing /> },
            { path: "referrals",                              element: <Referrals /> },
            { path: "plans",                                  element: <Plans /> },
            { path: "subscriptions",                          element: <Navigate to="/admin/reports?tab=subscriptions" replace /> },
            { path: "reports",                                element: <Reports /> },
            { path: "tickets",                                element: <Tickets /> },
            { path: "tickets/:id",                            element: <TicketDetails /> },
            { path: "notifications",                          element: <Notifications /> },
            { path: "home-content",                           element: <HomeContent /> },
            { path: "audit-logs",                             element: <AuditLogs /> },
            { path: "system-health",                          element: <SystemHealth /> },
            { path: "settings",                               element: <Settings /> },
            { path: "profile",                                element: <Profile /> },
            { path: "*",                                      element: <Navigate to="/admin/dashboard" replace /> },
        ],
    },
]);

export default router;
